// Throw this with a union type to get TypeScript to error when code path was not implemented
export const unreachable = (_x: never) => new Error("Unreachable code path");

export function nameOf<T>(key: keyof T): string {
  return key as string;
}

export function isDefined<T>(val: T): val is NonNullable<T> {
  return val !== null && val !== undefined;
}

export function assertIsDefined<T>(
  val: T,
  message = "Unexpected null or undefined value",
): asserts val is NonNullable<T> {
  if (!isDefined(val)) {
    throw new Error(message);
  }
}

export function isString(val: unknown): val is string {
  return typeof val === "string";
}

export function assertIsString(val: unknown): asserts val is string {
  if (!isString(val)) {
    throw new Error(`Value expected to be a string. Found ${typeof val} instead.`);
  }
}

export function isObject(val: unknown): val is object {
  return typeof val === "object";
}

export function assertIsObject(val: unknown): asserts val is object {
  if (!isObject(val)) {
    throw new Error(`Value expected to be a object. Found ${typeof val} instead.`);
  }
}

export function isEnumValue<T extends Record<string, unknown>>(
  target: T,
  value: unknown,
): value is T[keyof T] {
  return Object.values(target).includes(value);
}

export function assertIsEnumValue<T extends Record<string, unknown>>(
  target: T,
  value: unknown,
): asserts value is T[keyof T] {
  if (!isEnumValue(target, value)) {
    throw new Error(`Unsupported value ${String(value)}`);
  }
}

export function hasProperty<P extends string>(
  value: object | undefined,
  prop: P,
): value is Record<P, unknown> {
  return isDefined(value) && prop in value;
}

export function assertHasProperty<P extends string>(
  value: object | undefined,
  prop: P,
): asserts value is Record<P, unknown> {
  if (!hasProperty(value, prop)) {
    throw new Error(`Object is missing property ${prop}`);
  }
}

// NOTE: ts doesn't provide a typed Object.entries because it would look like:
// [key: keyof T, value: a type that merges all value types].
// having the following utils works at the cost of an extra check at runtime.
type Entries<T> = {
  [K in keyof T]-?: [K, T[K]];
}[keyof T][];

export function objectEntries<T extends object>(obj: T) {
  return Object.entries(obj) as Entries<T>;
}

export function objectSet<T extends object, K extends keyof T>(obj: T, key: K, value: T[K]) {
  if (key in obj && typeof obj[key] === typeof value) {
    obj[key] = value;
  }
}

export function toError(error: unknown): Error {
  if (error instanceof Error) return error;

  function hasMessage(error: unknown): error is { message: string } {
    return (
      typeof error === "object" &&
      error !== null &&
      "message" in error &&
      typeof (error as Record<string, unknown>).message === "string"
    );
  }

  if (hasMessage(error)) {
    return new Error(error.message);
  }

  try {
    return new Error(JSON.stringify(error));
  } catch {
    return new Error(String(error));
  }
}

export function omit<T extends object, P extends keyof T>(t: T, keys: P[]): Omit<T, P> {
  const result = {} as Omit<T, P>;
  const keySet = new Set(keys);

  for (const key in t) {
    if (Object.prototype.hasOwnProperty.call(t, key) && !keySet.has(key as unknown as P)) {
      (result as Record<string, unknown>)[key] = t[key];
    }
  }
  return result;
}

export function suffix<T extends object, P extends keyof T, S extends string>(
  suffix: S,
  t: T,
  keys: P[],
): Omit<T, P> & { [K in P as `${string & K}${S}`]: T[K] } {
  return {
    ...omit(t, keys),
    ...(Object.fromEntries(keys.map((key) => [`${String(key)}${suffix}`, t[key]])) as {
      [K in P as `${string & K}${S}`]: T[K];
    }),
  };
}

export const getRandomElement = <T>(array: T[]) => array[Math.floor(Math.random() * array.length)];

export function measureTime<T>(label: string, fn: () => T, log?: (message: string) => void): T;
export function measureTime<T>(
  label: string,
  fn: () => Promise<T>,
  log?: (message: string) => void,
): Promise<T>;
export function measureTime<T>(
  label: string,
  fn: () => T | Promise<T>,
  // eslint-disable-next-line no-console
  log = console.log,
): T | Promise<T> {
  const start = performance.now();

  const result = fn();

  if (result instanceof Promise) {
    return result.finally(() => {
      const duration = (performance.now() - start) / 1000;
      log(`${label}: ${duration.toFixed(2)}s`);
    });
  } else {
    const duration = (performance.now() - start) / 1000;
    log(`${label}: ${duration.toFixed(2)}s`);
    return result;
  }
}
